import React, { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Button, Spinner } from "reactstrap";
import { Table, Space, Drawer, Alert, Tooltip } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { Icon } from "../../../../components/Component";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import SiteStructureBulkUpload from "./SiteStructureBulkUpload";
import AddSiteStructureForm from "./AddSiteStructureForm";

interface SubUnit {
  key: string;
  subUnitName: string;
  unit: number;
  floorNo?: string;
  plannedStartDate?: string;
  plannedEndDate?: string;
}

interface SiteStructure {
  key: string;
  id?: string;
  unitName: string;
  unitQty: number;
  noOfFloors?: number;
  unitPerFloor?: number;
  plannedStartDate?: string;
  plannedEndDate?: string;
  isChildExist?: boolean;
  subUnits?: SubUnit[];
  errorFlag?: boolean;
  errorMessage?: string | string[];
}

interface UploadResult {
  newUnits: SiteStructure[];
  duplicateUnits: SiteStructure[];
  skippedUnits: SiteStructure[];
}

const StructureConfiguration: React.FC<any> = (props) => {
  const { handleSubmit, watch, getValues, setValue } = useFormContext();
  const projectType = watch("projectType");
  const prevProjectTypeRef = useRef<string | null>(null);
  const isInitialLoadRef = useRef(true);
  const hasSequence = watch("hasSequence");

  const [siteStructureData, setSiteStructureData] = useState<SiteStructure[]>(
    []
  );
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingRecord, setEditingRecord] = useState<SiteStructure | null>(
    null
  );
  const [uploadAlert, setUploadAlert] = useState<{
    type: string;
    message: string;
  } | null>(null);

  const getUnitTypeName = () => {
    switch (projectType) {
      case "1":
        return "Villa";
      case "2":
        return "Tower";
      case "3":
        return "Block";
      case "4":
        return "Tower";
      case "5":
        return "Phase";
      default:
        return "Unit";
    }
  };

  const getSubUnitTitle = () => {
    switch (projectType) {
      case "2":
      case "3":
        return "FLATS";
      case "4":
        return "SHOPS";
      case "5":
        return "MILESTONES";
      default:
        return "";
    }
  };

  const transformApiDataToTableData = (apiData: any): SiteStructure[] => {
    if (!apiData) return [];
    const arrayData = Array.isArray(apiData) ? apiData : [apiData];

    return arrayData.map((unit: any, idx: number) => {
      let subUnits: SubUnit[] = [];

      if (
        projectType === "5" &&
        unit.milestones &&
        Array.isArray(unit.milestones)
      ) {
        subUnits = unit.milestones.map((milestone: any, i: number) => ({
          key: milestone.id
            ? `sub_${milestone.id}`
            : `sub_${idx}_${i}_${Date.now()}_${Math.random()}`,
          subUnitName: milestone.milestoneName || milestone.name || "",
          unit:
            milestone.cubicMeters || milestone.quantity || milestone.unit || 0,
          floorNo: "",
          plannedStartDate: milestone.plannedStartDate || milestone.startDate,
          plannedEndDate: milestone.plannedEndDate || milestone.endDate,
          description:
            milestone.milestoneDescription || milestone.description || "",
        }));
      } else if (Array.isArray(unit.subUnits)) {
        subUnits = unit.subUnits.map((s: any, i: number) => ({
          key: s.id
            ? `sub_${s.id}`
            : `sub_${idx}_${i}_${Date.now()}_${Math.random()}`,
          subUnitName: s.subUnitName || s.name || "",
          unit: s.unit || s.size || 0,
          floorNo: s.floorNo || s.floor || "",
          floorType: s.floorType || "",
          plannedStartDate: s.plannedStartDate || s.startDate,
          plannedEndDate: s.plannedEndDate || s.endDate,
          description: s.description || "",
        }));
      }

      return {
        key: unit.id
          ? `unit_${unit.id}`
          : `temp_${Date.now()}_${idx}_${Math.random()}`,
        id: unit.id,
        unitName: unit.unitName || unit.phaseName || "",
        unitQty: unit.unitQty || unit.quantity || 0,
        noOfFloors: unit.noOfFloors || "",
        unitPerFloor: unit.unitPerFloor || "",
        plannedStartDate: unit.plannedStartDate || "N/A",
        plannedEndDate: unit.plannedEndDate || "N/A",
        isChildExist: unit.isChildExist || subUnits.length > 0,
        errorFlag: unit.errorFlag || false,
        errorMessage: unit.errorMessage,
        subUnits: subUnits,
      };
    });
  };

  const detectAndFilterDuplicates = (
    uploadedData: SiteStructure[],
    existingData: SiteStructure[]
  ): UploadResult => {
    const result: UploadResult = {
      newUnits: [],
      duplicateUnits: [],
      skippedUnits: [],
    };

    uploadedData.forEach((uploadedUnit) => {
      const isDuplicate = existingData.some(
        (existingUnit) =>
          existingUnit.unitName.toLowerCase().trim() ===
          uploadedUnit.unitName.toLowerCase().trim()
      );

      if (isDuplicate) {
        result.duplicateUnits.push(uploadedUnit);
      } else {
        result.newUnits.push(uploadedUnit);
      }
    });

    return result;
  };

  const smartMergeData = (
    uploadedData: SiteStructure[],
    existingData: SiteStructure[]
  ): SiteStructure[] => {
    const { newUnits, duplicateUnits } = detectAndFilterDuplicates(
      uploadedData,
      existingData
    );
    return [...existingData, ...newUnits];
  };

  useEffect(() => {
    const formData = getValues();

    const shouldClearTable = sessionStorage.getItem("shouldClearTable");

    // Don't load data if we're about to clear the table
    if (shouldClearTable === "true") {
      return;
    }

    if (formData.siteStructures?.length > 0) {
      setSiteStructureData(formData.siteStructures);
      return;
    }

    const apiData =
      formData.siteStructures?.length > 0
        ? formData.siteStructures
        : props.siteStructureData?.data?.allUnits || [];

    if (apiData?.length) {
      const transformed = transformApiDataToTableData(apiData);
      setSiteStructureData(transformed);
      setValue("siteStructures", transformed);
    }
  }, [props.siteStructureData, projectType, getValues, setValue]);

  useEffect(() => {
    if (siteStructureData.length > 0) {
      setValue("siteStructures", siteStructureData);
    }
  }, [siteStructureData, setValue]);

  // useEffect(() => {
  //   if (!projectType) return;
  //   setSiteStructureData([]);
  //   setValue("siteStructures", []);
  //   setUploadAlert(null);
  // }, [projectType]);

  useEffect(() => {
    const storedType = sessionStorage.getItem("prevProjectType");
    const shouldClearTable = sessionStorage.getItem("shouldClearTable");

    if (!storedType) {
      sessionStorage.setItem("prevProjectType", String(projectType));
      return;
    }

    if (shouldClearTable === "true") {
      setSiteStructureData([]);
      setValue("siteStructures", []);
      setUploadAlert(null);
      // Remove the flag after clearing
      sessionStorage.removeItem("shouldClearTable");
    }

    sessionStorage.setItem("prevProjectType", String(projectType));
  }, [projectType, setValue, siteStructureData.length]);

  const handleExpand = (expanded: boolean, record: SiteStructure) => {
    if (expanded) setExpandedRowKeys((prev) => [...prev, record.key]);
    else setExpandedRowKeys((prev) => prev.filter((key) => key !== record.key));
  };

  // === MAIN TABLE COLUMNS ===
  const columns = [
    {
      title: `${getUnitTypeName()} Name`,
      dataIndex: "unitName",
      key: "unitName",
      fixed: "left" as const,
      width: 120,
    },
    {
      title: "Quantity",
      dataIndex: "unitQty",
      key: "unitQty",
      width: 100,
    },
    ...(projectType !== "1" && projectType !== "5"
      ? [
        {
          title: "Floors",
          dataIndex: "noOfFloors",
          key: "noOfFloors",
          width: 100,
        },
      ]
      : []),
    {
      // title: "Units/Floor",
      title: (() => {
        switch (projectType) {
          case "1": // Villa
            return null;
          case "2": // Highrise
          case "3": // Standalone
            return "Flats per Floor";
          case "4": // Commercial
            return "Shops per Floor";
          case "5": // Infrastructure
            return "Milestones per Phase";
          default:
            return "Units per Floor";
        }
      })(),
      dataIndex: "unitPerFloor",
      key: "unitPerFloor",
      width: 100,
      ...(projectType === "1" ? { className: "d-none" } : {}),
      render: (text: any, record: SiteStructure) => {
        if (projectType === "5") {
          return record.subUnits?.length || 0;
        }
        return text || 0;
      },
    },
    {
      title: "Planned Start Date",
      dataIndex: "plannedStartDate",
      key: "plannedStartDate",
      width: 100,
      render: (text: any) =>
        text && text !== "N/A" ? dayjs(text).format("DD-MMM-YYYY") : "N/A",
    },
    {
      title: "Planned End Date",
      dataIndex: "plannedEndDate",
      key: "plannedEndDate",
      width: 100,
      render: (text: any) =>
        text && text !== "N/A" ? dayjs(text).format("DD-MMM-YYYY") : "N/A",
    },
    {
      title: "Status",
      dataIndex: "errorFlag",
      key: "errorFlag",
      width: 80,
      align: "center" as const,
      render: (errorFlag: boolean, record: any) => {
        if (errorFlag && record.errorMessage) {
          const errorMessages = Array.isArray(record.errorMessage)
            ? record.errorMessage
            : [record.errorMessage];

          const tooltipContent = (
            <div style={{ maxWidth: 300 }}>
              <div
                style={{
                  fontWeight: "bold",
                  marginBottom: 8,
                  color: "#ff4d4f",
                }}
              >
                Validation Errors:
              </div>
              {errorMessages.map((error: string, index: number) => (
                <div key={index} style={{ marginBottom: 4 }}>
                  • {error}
                </div>
              ))}
            </div>
          );

          return (
            <Tooltip title={tooltipContent} placement="topLeft">
              <ExclamationCircleOutlined
                style={{
                  color: "#ff4d4f",
                  fontSize: 18,
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          );
        }

        return (
          <Tooltip title="No validation errors">
            <CheckCircleOutlined
              style={{
                color: "#52c41a",
                fontSize: 18,
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: "right" as const,
      render: (_: any, record: SiteStructure) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <a onClick={() => openForm(record)}>
              <EditOutlined />
            </a>
          </Tooltip>
          <Tooltip title="Delete">
            <a onClick={() => handleDelete(record.key)}>
              <DeleteOutlined />
            </a>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // === SUB-UNIT COLUMNS ===
  const getSubColumns = () => {
    const title = getSubUnitTitle();
    if (!title) return [];

    if (title === "MILESTONES") {
      return [
        {
          title: "Milestone Name",
          dataIndex: "subUnitName",
          key: "subUnitName",
          width: 100,
        },
        { title: "Cubic Meters", dataIndex: "unit", key: "unit", width: 100 },
        {
          title: "Planned Start Date",
          dataIndex: "plannedStartDate",
          key: "plannedStartDate",
          width: 100,
          render: (text: any) =>
            text ? dayjs(text).format("DD-MMM-YYYY") : "N/A",
        },
        {
          title: "Planned End Date",
          dataIndex: "plannedEndDate",
          key: "plannedEndDate",
          width: 100,
          render: (text: any) =>
            text ? dayjs(text).format("DD-MMM-YYYY") : "N/A",
        },
      ];
    }

    return [
      {
        title:
          title === "FLATS"
            ? "Flat Number"
            : title === "SHOPS"
              ? "Shop Number"
              : "Sub-Unit",
        dataIndex: "subUnitName",
        key: "subUnitName",
        width: 120,
      },
      { title: "Square Feet", dataIndex: "unit", key: "unit", width: 100 },
      { title: "Floor", dataIndex: "floorNo", key: "floorNo", width: 100 },
      ...(projectType === "2" || projectType === "3" || projectType === "4"
        ? [
          {
            title: "Floor Type",
            dataIndex: "floorType",
            key: "floorType",
            width: 100,
            render: (text: any) => {
              if (!text) return "N/A";
              const floorTypeMap: { [key: string]: string } = {
                residential: "Residential",
                commercial: "Commercial",
                parking: "Parking",
              };
              return floorTypeMap[text] || text;
            },
          },
        ]
        : []),
      {
        title: "Planned Start Date",
        dataIndex: "plannedStartDate",
        key: "plannedStartDate",
        width: 100,
        render: (text: any) =>
          text ? dayjs(text).format("DD-MMM-YYYY") : "N/A",
      },
      {
        title: "Planned End Date",
        dataIndex: "plannedEndDate",
        key: "plannedEndDate",
        width: 100,
        render: (text: any) =>
          text ? dayjs(text).format("DD-MMM-YYYY") : "N/A",
      },
    ];
  };

  const openForm = (record?: SiteStructure) => {
    setEditingRecord(record || null);
    setShowAddDrawer(true);
  };

  const handleDelete = (key: string) => {
    setSiteStructureData((prevData) => {
      const updatedData = prevData.filter((item) => item.key !== key);
      setValue("siteStructures", updatedData);
      return [...updatedData];
    });

    setUploadAlert({
      type: "success",
      message: `${getUnitTypeName()} has been deleted successfully.`,
    });

    setTimeout(() => setUploadAlert(null), 4000);
  };

  const handleBulkUpload = (uploaded: any[]) => {
    const processedData = uploaded.map((item) => {
      if (projectType === "5") {
        return {
          ...item,
          subUnits: item.milestones || item.subUnits || [],
        };
      }
      return item;
    });

    const transformed = transformApiDataToTableData(processedData);

    const { newUnits, duplicateUnits } = detectAndFilterDuplicates(
      transformed,
      siteStructureData
    );

    if (newUnits.length === 0 && duplicateUnits.length > 0) {
      setUploadAlert({
        type: "warning",
        message: `All ${duplicateUnits.length
          } ${getUnitTypeName()}(s) already exist. No new data added.`,
      });
      return;
    }

    const updated = smartMergeData(transformed, siteStructureData);
    setSiteStructureData(updated);
    setValue("siteStructures", updated);

    if (newUnits.length > 0 && duplicateUnits.length > 0) {
      setUploadAlert({
        type: "info",
        message: `${newUnits.length} new ${getUnitTypeName()}(s) added, ${duplicateUnits.length
          } duplicate(s) skipped.`,
      });
    } else if (newUnits.length > 0) {
      setUploadAlert({
        type: "success",
        message: `${newUnits.length} ${getUnitTypeName()}(s) has been added.`,
      });
    }
    setTimeout(() => setUploadAlert(null), 5000);
  };

  const onSubmit = async (data: any) => {
    if (siteStructureData.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Structures Added",
        text: `Please add at least one ${getUnitTypeName()} before proceeding.`,
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    const hasErrors = siteStructureData.some((item) => item.errorFlag === true);
    if (hasErrors) {
      Swal.fire({
        icon: "error",
        title: "Validation Errors",
        text: "Please fix all validation errors before proceeding.",
        confirmButtonColor: "#d33",
      });
      return;
    }

    try {
      setIsLoading(true);
      setValue("siteStructures", siteStructureData);
      const formData = getValues();

      const payload = {
        ...formData,
        siteStructures: siteStructureData,
        projectType,
        ...(formData.hasSequence === false && {
          siteStructureSequenceId: formData.siteStructureSequenceId,
        }),
      } as any;

      if (formData.hasSequence === false) {
        payload.siteStructureSequenceId = formData.siteStructureSequenceId;
      }
      
      await props.onSubmit(payload);
      sessionStorage.removeItem("prevProjectType");
      sessionStorage.removeItem("hasChangedProjectType");
    } catch (err) {
      console.error("Submit error:", err);
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: "An error occurred while submitting the form.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {!projectType ? (
        <Alert
          message="Please select a project type to configure structure."
          type="info"
          showIcon
        />
      ) : (
        <>
          {isLoading ? (
            <div className="text-center p-4">
              <Spinner color="primary" /> Loading structures...
            </div>
          ) : (
            <>
              {/* Upload Alert */}
              {uploadAlert && (
                <div className="mb-3">
                  <Alert
                    message={uploadAlert.message}
                    type={
                      uploadAlert.type === "success"
                        ? "success"
                        : uploadAlert.type === "warning"
                          ? "warning"
                          : "info"
                    }
                    showIcon
                    closable
                    onClose={() => setUploadAlert(null)}
                  />
                </div>
              )}

              <div
                className="d-flex justify-content-end align-items-center mb-3"
                style={{ gap: "15px", marginBottom: "16px" }}
              >
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => openForm()}
                  className="d-flex align-items-center"
                  style={{ padding: "6px 14px" }}
                >
                  <Icon name="plus" className="me-1" />
                  {/* Add New {getUnitTypeName()} */}
                  Add
                </Button>

                <div style={{ display: "flex", alignItems: "center" }}>
                  <SiteStructureBulkUpload
                    onUpload={handleBulkUpload}
                    projectType={projectType}
                  />
                </div>
              </div>

              <Table
                columns={columns}
                dataSource={siteStructureData}
                scroll={{ x: 1000 }}
                bordered
                size="small"
                expandable={{
                  expandedRowKeys,
                  onExpand: handleExpand,
                  expandedRowRender: (record) =>
                    record.subUnits?.length && getSubUnitTitle() ? (
                      <>
                        <h6 className="mt-2 mb-2">{getSubUnitTitle()}</h6>
                        <Table
                          columns={getSubColumns()}
                          dataSource={record.subUnits}
                          pagination={false}
                          size="small"
                          bordered
                        />
                      </>
                    ) : null,
                  rowExpandable: (record) =>
                    Array.isArray(record.subUnits) &&
                    record.subUnits.length > 0 &&
                    !!getSubUnitTitle(),
                }}
                locale={{
                  emptyText: `Please click "Add New ${getUnitTypeName()}" or upload a file.`,
                }}
                pagination={false}
              />
            </>
          )}

          <Drawer
            title={
              editingRecord
                ? `Edit ${getUnitTypeName()}`
                : `Add ${getUnitTypeName()}`
            }
            placement="right"
            onClose={() => {
              setShowAddDrawer(false);
              setEditingRecord(null);
            }}
            open={showAddDrawer}
            width={1200}
            destroyOnClose
          >
            <AddSiteStructureForm
              projectType={projectType}
              editData={editingRecord}
              onSave={(newStructure) => {
                if (editingRecord) {
                  const updated = siteStructureData.map((item) => {
                    if (item.key === editingRecord.key) {
                      return {
                        ...item,
                        ...newStructure,
                        subUnits:
                          newStructure.subUnits !== undefined
                            ? newStructure.subUnits?.map(
                              (sub: any, idx: number) => ({
                                key:
                                  sub.key ||
                                  sub.id ||
                                  `sub_${item.key}_${idx}`,
                                subUnitName: sub.name || sub.subUnitName,
                                unit: sub.size || sub.unit,
                                floorNo: sub.floor || sub.floorNo,
                                plannedStartDate:
                                  sub.startDate || sub.plannedStartDate,
                                plannedEndDate:
                                  sub.endDate || sub.plannedEndDate,
                                description: sub.description,
                              })
                            )
                            : item.subUnits,
                      };
                    }
                    return item;
                  });

                  setSiteStructureData(updated);
                  setValue("siteStructures", updated);

                  Swal.fire({
                    icon: "success",
                    title: "Success!",
                    text: `${getUnitTypeName()} "${newStructure.unitName
                      }" has been updated.`,
                    timer: 2000,
                    showConfirmButton: false,
                  });
                } else {
                  const unitKey = `unit_${Date.now()}`;
                  const transformedStructure = {
                    ...newStructure,
                    key: unitKey,
                    subUnits:
                      newStructure.subUnits?.map((sub: any, idx: number) => ({
                        key: `sub_${unitKey}_${idx}`,
                        subUnitName: sub.name,
                        unit: sub.size,
                        floorNo: sub.floor,
                        plannedStartDate: sub.startDate,
                        plannedEndDate: sub.endDate,
                        description: sub.description,
                      })) || [],
                    errorFlag: false,
                  };
                  const updatedList = [
                    ...siteStructureData,
                    transformedStructure,
                  ];
                  setSiteStructureData(updatedList);
                  setValue("siteStructures", updatedList);

                  Swal.fire({
                    icon: "success",
                    title: "Success!",
                    text: `${getUnitTypeName()} "${newStructure.unitName
                      }" has been added.`,
                    timer: 2000,
                    showConfirmButton: false,
                  });
                }

                setShowAddDrawer(false);
                setEditingRecord(null);
              }}
              onCancel={() => {
                setShowAddDrawer(false);
                setEditingRecord(null);
              }}
            />
          </Drawer>

          <div className="actions clearfix">
            <ul>
              <li>
                <Button
                  disabled={siteStructureData.length === 0}
                  color="primary"
                  type="submit"
                >
                  Submit
                </Button>
              </li>
              <li>
                <Button color="primary" onClick={props.prev}>
                  Previous
                </Button>
              </li>
            </ul>
          </div>
        </>
      )}
    </form>
  );
};

export default React.memo(StructureConfiguration);
